// Lazy, opt-in analytics loader.
//
// Why this exists:
//   The About panel promises telemetry is "off by default", "anonymous",
//   "no IP linking", and captures "nothing that could identify you".
//   We honour that by loading PostHog *only* after the user has explicitly
//   enabled the telemetry toggle, and by disabling session recording and
//   autocapture even after they opt in. Nothing runs on first paint.
//
// Anyone reading this file: please keep it default-off. This app serves
// people in the middle of a manic episode — trust matters.

import { loadTelemetryPreference } from "./telemetry";

const POSTHOG_KEY = "phc_DbsPb39SRc8z3EiQ6Dhj6ikv4H4rTKcht9d4sZSesceP";
const POSTHOG_HOST = "https://ap.emergent.sh";

let loadPromise = null;
let initialised = false;

// Load the PostHog SDK on demand. Idempotent — repeated calls share the
// same promise so we never inject two <script> tags.
const loadPostHogScript = () =>
  new Promise((resolve, reject) => {
    if (window.posthog && window.posthog.__loaded) {
      resolve(window.posthog);
      return;
    }
    // Inject the tiny stub + real bundle via PostHog's official snippet.
    const stub = {
      _i: [],
      __SV: 1,
    };
    window.posthog = window.posthog || stub;
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.crossOrigin = "anonymous";
    script.async = true;
    script.src = `${POSTHOG_HOST.replace(".i.posthog.com", "-assets.i.posthog.com")}/static/array.js`;
    script.onload = () => resolve(window.posthog);
    script.onerror = reject;
    const first = document.getElementsByTagName("script")[0];
    if (first && first.parentNode) {
      first.parentNode.insertBefore(script, first);
    } else {
      document.head.appendChild(script);
    }
  });

const initPostHog = () => {
  if (initialised || !window.posthog || typeof window.posthog.init !== "function") return;
  try {
    window.posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // Never build person profiles from anonymous traffic.
      person_profiles: "identified_only",
      // Fully disable session replays / DOM capture. This is the biggest
      // privacy footgun in analytics libraries and there is no product
      // reason to record a distressed user's screen.
      disable_session_recording: true,
      autocapture: false,
      capture_pageview: true,
      capture_pageleave: false,
      // Do not persist across sessions or devices — every visit is fresh.
      persistence: "memory",
      // Belt and braces: strip anything that looks identifying just in case.
      property_blacklist: [
        "$ip",
        "$geoip_city_name",
        "$geoip_country_name",
        "$geoip_country_code",
        "$geoip_continent_name",
        "$geoip_continent_code",
        "$geoip_postal_code",
        "$geoip_latitude",
        "$geoip_longitude",
        "$geoip_time_zone",
        "$geoip_subdivision_1_name",
        "$geoip_subdivision_1_code",
      ],
    });
    initialised = true;
  } catch (_) {
    // Silent — a failed analytics init must never disrupt the calm.
  }
};

// Public: called once on app boot. Only activates PostHog if the user has
// already opted in in a previous visit; otherwise it is a no-op.
export const initAnalyticsIfOptedIn = () => {
  if (typeof window === "undefined") return;
  if (!loadTelemetryPreference()) return;
  if (loadPromise) return loadPromise;
  loadPromise = loadPostHogScript()
    .then(initPostHog)
    .catch(() => { /* silent */ });
  return loadPromise;
};

// Public: called from the About toggle when a user turns telemetry ON.
export const enableAnalytics = () => {
  if (typeof window === "undefined") return;
  if (loadPromise) {
    // Already loaded — just resume capture.
    try { window.posthog && window.posthog.opt_in_capturing && window.posthog.opt_in_capturing(); } catch (_) { /* noop */ }
    return loadPromise;
  }
  loadPromise = loadPostHogScript()
    .then(initPostHog)
    .catch(() => { /* silent */ });
  return loadPromise;
};

// Public: called from the About toggle when a user turns telemetry OFF.
// Stops any further capture immediately and resets identifiers.
//
// Order matters: reset() FIRST (clears the in-memory persistence store,
// including any prior distinct_id), then opt_out_capturing() LAST so the
// opt-out flag survives — because `persistence: 'memory'` means both live
// in the same bag and a later reset() would wipe the flag.
export const disableAnalytics = () => {
  if (typeof window === "undefined") return;
  try {
    if (window.posthog) {
      if (typeof window.posthog.reset === "function") {
        window.posthog.reset(true);
      }
      if (typeof window.posthog.opt_out_capturing === "function") {
        window.posthog.opt_out_capturing();
      }
    }
  } catch (_) { /* silent */ }
};
